import React from 'react';
import styled from 'styled-components';
import type { VoiceState } from '../hooks/useVoiceChat';

const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  width: 100%;
`;

const Indicator = styled.button<{ $tone?: 'record' | 'speak' | 'wait' }>`
  border: none;
  background: none;
  padding: 0;
  font-size: 0.75rem;
  color: ${({ $tone }) =>
    $tone === 'record'
      ? 'var(--danger, #dc2626)'
      : $tone === 'speak'
        ? 'var(--success, #16a34a)'
        : 'var(--accent)'};
  cursor: ${({ $tone }) => ($tone === 'speak' ? 'pointer' : 'default')};
`;

const ErrorText = styled.div`
  font-size: 0.75rem;
  color: var(--danger, #dc2626);
`;

const MicButton = styled.button<{ $recording?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  flex-shrink: 0;
  border-radius: 10px;
  background: ${({ $recording }) =>
    $recording ? 'var(--danger, #ef4444)' : 'var(--bg-surface, transparent)'};
  color: ${({ $recording }) =>
    $recording ? 'white' : 'var(--text-muted)'};
  border: 1px solid
    ${({ $recording }) =>
      $recording ? 'var(--danger, #ef4444)' : 'var(--border)'};
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    border-color: ${({ $recording }) =>
      $recording ? 'var(--danger, #dc2626)' : 'var(--accent)'};
    color: ${({ $recording }) => ($recording ? 'white' : 'var(--accent)')};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export interface VoiceMicButtonProps {
  recording: boolean;
  disabled?: boolean;
  testId?: string;
  onClick?: () => void;
}

export const VoiceMicButton: React.FC<VoiceMicButtonProps> = ({
  recording,
  disabled,
  testId = 'voice-mic',
  onClick,
}) => (
  <MicButton
    type="button"
    $recording={recording}
    onClick={onClick}
    disabled={disabled}
    aria-label={recording ? 'Stop recording' : 'Start recording'}
    aria-pressed={recording}
    data-testid={testId}
    title={recording ? 'Stop recording' : 'Talk'}
  >
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M12 14a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v5a3 3 0 0 0 3 3Zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-3.08A7 7 0 0 0 19 11h-2Z" />
    </svg>
  </MicButton>
);

export interface VoiceChatBarProps {
  voiceState: VoiceState;
  error?: string | null;
  onStopSpeaking?: () => void;
}

const VoiceChatBar: React.FC<VoiceChatBarProps> = ({
  voiceState,
  error,
  onStopSpeaking,
}) => {
  if (
    voiceState !== 'recording' &&
    voiceState !== 'transcribing' &&
    voiceState !== 'speaking' &&
    !error
  ) {
    return null;
  }

  return (
    <Row>
      {voiceState === 'recording' && (
        <Indicator $tone="record" type="button" disabled>
          Recording… tap the mic to stop. You can still type.
        </Indicator>
      )}
      {voiceState === 'transcribing' && (
        <Indicator $tone="wait" type="button" disabled>
          Transcribing…
        </Indicator>
      )}
      {voiceState === 'speaking' && (
        <Indicator
          $tone="speak"
          type="button"
          onClick={onStopSpeaking}
          data-testid="voice-speaking"
        >
          Speaking… click to stop
        </Indicator>
      )}
      {error && <ErrorText data-testid="voice-error">{error}</ErrorText>}
    </Row>
  );
};

export default VoiceChatBar;
